import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Select from '../../components/ui/Select';
import { API_ENDPOINTS } from '../../utils/config';

const TABS = [
  { key: 'surveys', label: 'Surveys', icon: 'ClipboardList' },
  { key: 'swap_requests', label: 'Swap Requests', icon: 'ArrowLeftRight' },
  { key: 'comments', label: 'Comments', icon: 'MessageSquare' },
];

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const StatusBadge = ({ status, colorMap }) => {
  const config = colorMap[status] || colorMap.default;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      <Icon name={config.icon} size={12} className="mr-1" />
      {status.replace('_', ' ').toUpperCase()}
    </span>
  );
};

// ─── Shared SearchBar ───────────────────────────────────────────────────────

const SearchBar = ({ value, onChange, placeholder }) => (
  <div className="relative">
    <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full pl-9 pr-9 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
    />
    {value && (
      <button onClick={() => onChange('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
        <Icon name="X" size={14} />
      </button>
    )}
  </div>
);

// ─── Surveys Tab ────────────────────────────────────────────────────────────

const surveyStatusMap = {
  pending:     { color: 'bg-yellow-100 text-yellow-800', icon: 'Clock' },
  in_progress: { color: 'bg-blue-100 text-blue-800',   icon: 'Play' },
  completed:   { color: 'bg-green-100 text-green-800', icon: 'CheckCircle' },
  cancelled:   { color: 'bg-red-100 text-red-800',     icon: 'XCircle' },
  default:     { color: 'bg-yellow-100 text-yellow-800', icon: 'Clock' },
};

const SurveysTab = () => {
  const [surveys, setSurveys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSurveys, setSelectedSurveys] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchSurveys = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await axios.get(API_ENDPOINTS.SURVEYS_LIST, { headers: getAuthHeaders() });
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || response.data?.data || response.data?.surveys || []);
      setSurveys(data);
    } catch {
      setError('Failed to load surveys. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchSurveys(); }, [fetchSurveys]);

  const filteredSurveys = searchQuery.trim()
    ? surveys.filter(s => {
        const q = searchQuery.toLowerCase();
        return (
          s.survey_number?.toLowerCase().includes(q) ||
          s.surveyor?.username?.toLowerCase().includes(q) ||
          s.customer_name?.toLowerCase().includes(q) ||
          s.property_address?.toLowerCase().includes(q)
        );
      })
    : surveys;

  const handleSelectSurvey = (id) =>
    setSelectedSurveys(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSelectAll = () =>
    setSelectedSurveys(selectedSurveys.length === filteredSurveys.length && filteredSurveys.length > 0 ? [] : filteredSurveys.map(s => s.id));

  if (isLoading) return (
    <div className="flex items-center justify-center py-16">
      <Icon name="Loader2" size={24} className="animate-spin mr-2" color="var(--color-primary)" />
      <span className="text-foreground">Loading surveys...</span>
    </div>
  );

  if (error) return (
    <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center space-x-2">
      <Icon name="AlertCircle" size={16} color="var(--color-red-600)" />
      <span className="text-red-700">{error}</span>
      <Button variant="ghost" size="xs" onClick={fetchSurveys}>Retry</Button>
    </div>
  );

  if (surveys.length === 0) return (
    <div className="text-center py-12">
      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon name="ClipboardList" size={24} color="var(--color-muted-foreground)" />
      </div>
      <h3 className="text-lg font-medium text-foreground mb-2">No surveys found</h3>
      <p className="text-muted-foreground mb-4">Get started by assigning your first survey to a property.</p>
      <Button variant="default" onClick={() => { window.location.href = '/property-management'; }} iconName="Plus" iconPosition="left">
        Assign Survey
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search by survey number, surveyor, or property..." />
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="bg-muted/30 px-6 py-3 border-b border-border flex items-center space-x-4">
        <input
          type="checkbox"
          checked={selectedSurveys.length === filteredSurveys.length && filteredSurveys.length > 0}
          onChange={handleSelectAll}
          className="rounded border-border text-primary focus:ring-primary"
        />
        <span className="text-sm font-medium text-muted-foreground">
          {selectedSurveys.length > 0 ? `${selectedSurveys.length} selected` : `${filteredSurveys.length} survey${filteredSurveys.length !== 1 ? 's' : ''}`}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/20">
            <tr>
              {['Survey Details', 'Property', 'Surveyor', 'Status', 'Created', 'Actions'].map(h => (
                <th key={h} className={`px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider ${h === 'Actions' ? 'text-right' : 'text-left'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredSurveys.length === 0 ? (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-sm text-muted-foreground">No surveys match your search.</td></tr>
            ) : filteredSurveys.map((survey) => (
              <tr key={survey.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <input type="checkbox" checked={selectedSurveys.includes(survey.id)} onChange={() => handleSelectSurvey(survey.id)} className="rounded border-border text-primary focus:ring-primary" />
                    <div>
                      <div className="text-sm font-medium text-foreground">{survey.survey_number}</div>
                      <div className="text-xs text-muted-foreground">ID: {survey.id}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-foreground">{survey.customer_name}</div>
                  <div className="text-xs text-muted-foreground max-w-xs truncate">{survey.property_address}</div>
                  <div className="text-xs text-muted-foreground">Contact: {survey.contact_person_name} ({survey.contact_person_phone_num})</div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="User" size={14} color="var(--color-primary)" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">{survey.surveyor?.username}</div>
                      <div className="text-xs text-muted-foreground">{survey.surveyor?.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <StatusBadge status={survey.status} colorMap={surveyStatusMap} />
                </td>
                <td className="px-6 py-4 text-sm text-foreground">{formatDate(survey.created_at)}</td>
                <td className="px-6 py-4 text-right">
                  <Button variant="ghost" size="sm" onClick={() => { window.location.href = `/field-survey?surveyId=${survey.id}`; }} iconName="Eye" iconPosition="left">
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );
};

// ─── Swap Requests Tab ───────────────────────────────────────────────────────

const swapStatusMap = {
  pending:  { color: 'bg-yellow-100 text-yellow-800', icon: 'Clock' },
  approved: { color: 'bg-green-100 text-green-800',  icon: 'CheckCircle' },
  rejected: { color: 'bg-red-100 text-red-800',      icon: 'XCircle' },
  default:  { color: 'bg-yellow-100 text-yellow-800', icon: 'Clock' },
};

const SwapRequestsTab = () => {
  const [requests, setRequests] = useState([]);
  const [surveyors, setSurveyors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [rejectLoading, setRejectLoading] = useState(null);
  const [approveModal, setApproveModal] = useState(null);
  const [selectedSurveyor, setSelectedSurveyor] = useState('');
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [reqRes, surveyorsRes] = await Promise.all([
        axios.get(API_ENDPOINTS.SWAP_REQUESTS_LIST, { headers: getAuthHeaders() }),
        axios.get(API_ENDPOINTS.SURVEYORS_LIST, { headers: getAuthHeaders() }),
      ]);
      const data = Array.isArray(reqRes.data)
        ? reqRes.data
        : (reqRes.data?.results || reqRes.data?.data || []);
      setRequests(data);
      const surveyorData = Array.isArray(surveyorsRes.data)
        ? surveyorsRes.data
        : (surveyorsRes.data?.results || surveyorsRes.data?.data || []);
      setSurveyors(surveyorData);
    } catch {
      setError('Failed to load swap requests. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const openApproveModal = (requestId) => {
    setApproveModal({ requestId });
    setSelectedSurveyor('');
    setApproveError('');
  };

  const closeApproveModal = () => {
    setApproveModal(null);
    setSelectedSurveyor('');
    setApproveError('');
  };

  const handleApprove = async () => {
    if (!selectedSurveyor) { setApproveError('Please select a surveyor.'); return; }
    setApproveLoading(true);
    setApproveError('');
    try {
      const response = await axios.patch(
        API_ENDPOINTS.SWAP_REQUEST_ACTION(approveModal.requestId),
        { assigned_to_id: Number(selectedSurveyor), status: 'approved' },
        { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      const updated = response.data?.data || response.data;
      setRequests(prev => prev.map(r => r.id === approveModal.requestId ? { ...r, ...updated } : r));
      closeApproveModal();
    } catch {
      setApproveError('Failed to approve swap request.');
    } finally {
      setApproveLoading(false);
    }
  };

  const handleReject = async (id) => {
    setRejectLoading(id);
    try {
      const response = await axios.patch(
        API_ENDPOINTS.SWAP_REQUEST_ACTION(id),
        { status: 'rejected' },
        { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      const updated = response.data?.data || response.data;
      setRequests(prev => prev.map(r => r.id === id ? { ...r, ...updated } : r));
    } catch {
      setError('Failed to reject swap request.');
    } finally {
      setRejectLoading(null);
    }
  };

  if (isLoading) return (
    <div className="flex items-center justify-center py-16">
      <Icon name="Loader2" size={24} className="animate-spin mr-2" color="var(--color-primary)" />
      <span className="text-foreground">Loading swap requests...</span>
    </div>
  );

  if (error) return (
    <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center space-x-2">
      <Icon name="AlertCircle" size={16} />
      <span className="text-red-700">{error}</span>
      <Button variant="ghost" size="xs" onClick={fetchRequests}>Retry</Button>
    </div>
  );

  if (requests.length === 0) return (
    <div className="text-center py-12">
      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon name="ArrowLeftRight" size={24} color="var(--color-muted-foreground)" />
      </div>
      <h3 className="text-lg font-medium text-foreground mb-1">No swap requests</h3>
      <p className="text-muted-foreground text-sm">Swap requests submitted by surveyors will appear here.</p>
    </div>
  );

  const filteredRequests = searchQuery.trim()
    ? requests.filter(r => {
        const q = searchQuery.toLowerCase();
        return (
          r.survey_number?.toLowerCase().includes(q) ||
          r.requested_by?.username?.toLowerCase().includes(q) ||
          r.assigned_to?.username?.toLowerCase().includes(q)
        );
      })
    : requests;

  return (
    <div className="space-y-4">
      <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search by survey number or surveyor..." />
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/20">
              <tr>
                {['Survey Number', 'Requested By', 'Reason', 'Status', 'Assigned To', 'Reviewed By', 'Date', 'Actions'].map(h => (
                  <th key={h} className={`px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider ${h === 'Actions' ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredRequests.length === 0 ? (
                <tr><td colSpan={8} className="px-6 py-10 text-center text-sm text-muted-foreground">No swap requests match your search.</td></tr>
              ) : filteredRequests.map((req) => (
                <tr key={req.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-foreground">{req.survey_number}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center">
                        <Icon name="User" size={13} color="var(--color-primary)" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground">{req.requested_by?.username}</div>
                        <div className="text-xs text-muted-foreground">{req.requested_by?.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-foreground max-w-xs">{req.reason}</p>
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={req.status} colorMap={swapStatusMap} />
                  </td>
                  <td className="px-6 py-4">
                    {req.assigned_to ? (
                      <div>
                        <div className="text-sm font-medium text-foreground">{req.assigned_to.username}</div>
                        <div className="text-xs text-muted-foreground">{req.assigned_to.email}</div>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {req.reviewed_by ? (
                      <div>
                        <div className="text-sm font-medium text-foreground">{req.reviewed_by.username}</div>
                        {req.reviewed_at && <div className="text-xs text-muted-foreground">{formatDate(req.reviewed_at)}</div>}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground whitespace-nowrap">
                    {formatDate(req.created_at)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {req.status === 'pending' ? (
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          variant="success"
                          size="xs"
                          disabled={!!rejectLoading}
                          onClick={() => openApproveModal(req.id)}
                          iconName="Check"
                          iconPosition="left"
                        >
                          Approve
                        </Button>
                        <Button
                          variant="danger"
                          size="xs"
                          loading={rejectLoading === req.id}
                          disabled={!!rejectLoading}
                          onClick={() => handleReject(req.id)}
                          iconName="X"
                          iconPosition="left"
                        >
                          Reject
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Approve Modal */}
      {approveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeApproveModal} />
          <div className="relative bg-card rounded-lg border border-border shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Icon name="ArrowLeftRight" size={18} color="var(--color-primary)" />
                <h3 className="text-base font-semibold text-foreground">Approve Swap Request</h3>
              </div>
              <button onClick={closeApproveModal} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={18} />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Select a surveyor to assign this survey to.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-1">Assign To</label>
              <Select
                value={selectedSurveyor}
                onChange={(value) => setSelectedSurveyor(value)}
                options={[
                  ...surveyors.map(s => ({ value: String(s.id), label: `${s.username}${s.email ? ` (${s.email})` : ''}` }))
                ]}
                placeholder="-- Select a surveyor --"
                searchable
              />
            </div>
            {approveError && (
              <p className="text-xs text-red-600 mb-3">{approveError}</p>
            )}
            <div className="flex justify-end space-x-2">
              <Button variant="outline" size="sm" onClick={closeApproveModal} disabled={approveLoading}>
                Cancel
              </Button>
              <Button
                variant="success"
                size="sm"
                loading={approveLoading}
                disabled={!selectedSurveyor || approveLoading}
                onClick={handleApprove}
                iconName="Check"
                iconPosition="left"
              >
                Confirm Approve
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Comments Tab ────────────────────────────────────────────────────────────

const CommentsTab = () => {
  const [surveys, setSurveys] = useState([]);
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchAllComments = async () => {
      setIsLoading(true);
      setError('');
      try {
        const survRes = await axios.get(API_ENDPOINTS.SURVEYS_LIST, { headers: getAuthHeaders() });
        const surveyList = Array.isArray(survRes.data)
          ? survRes.data
          : (survRes.data?.results || survRes.data?.data || survRes.data?.surveys || []);
        setSurveys(surveyList);

        const results = await Promise.all(
          surveyList.map(async (s) => {
            try {
              const res = await axios.get(API_ENDPOINTS.SURVEY_COMMENTS_LIST(s.id), { headers: getAuthHeaders() });
              return (res.data?.data || []).map(c => ({
                ...c,
                surveyId: s.id,
                surveyNumber: s.survey_number,
              }));
            } catch {
              return [];
            }
          })
        );
        setComments(results.flat().sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
      } catch {
        setError('Failed to load comments.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllComments();
  }, []);

  if (isLoading) return (
    <div className="flex items-center justify-center py-16">
      <Icon name="Loader2" size={24} className="animate-spin mr-2" color="var(--color-primary)" />
      <span className="text-foreground">Loading comments...</span>
    </div>
  );

  if (error) return (
    <div className="p-4 bg-red-50 border border-red-200 rounded-md flex items-center space-x-2">
      <Icon name="AlertCircle" size={15} />
      <span className="text-sm text-red-700">{error}</span>
    </div>
  );

  if (comments.length === 0) return (
    <div className="text-center py-12">
      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon name="MessageSquare" size={24} color="var(--color-muted-foreground)" />
      </div>
      <h3 className="text-lg font-medium text-foreground mb-1">No comments yet</h3>
      <p className="text-muted-foreground text-sm">Comments added by surveyors from the mobile app will appear here.</p>
    </div>
  );

  const filteredComments = searchQuery.trim()
    ? comments.filter(c => {
        const q = searchQuery.toLowerCase();
        return (
          c.surveyNumber?.toLowerCase().includes(q) ||
          c.author?.toLowerCase().includes(q) ||
          c.comment?.toLowerCase().includes(q)
        );
      })
    : comments;

  return (
    <div className="space-y-4">
      <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search by survey number, author, or comment..." />
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="bg-muted/30 px-6 py-3 border-b border-border">
        <span className="text-sm text-muted-foreground">{filteredComments.length} comment{filteredComments.length !== 1 ? 's' : ''} across {surveys.length} survey{surveys.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/20">
            <tr>
              {['Survey', 'Added By', 'Comment', 'Date'].map(h => (
                <th key={h} className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredComments.length === 0 ? (
              <tr><td colSpan={4} className="px-6 py-10 text-center text-sm text-muted-foreground">No comments match your search.</td></tr>
            ) : filteredComments.map((c, idx) => (
              <tr key={idx} className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-4">
                  <span className="text-sm font-medium text-foreground">{c.surveyNumber}</span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="User" size={13} color="var(--color-primary)" />
                    </div>
                    <span className="text-sm font-medium text-foreground">{c.author}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <p className="text-sm text-foreground">{c.comment}</p>
                </td>
                <td className="px-6 py-4 text-sm text-muted-foreground whitespace-nowrap">
                  {formatDate(c.timestamp)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const Surveys = () => {
  const [activeTab, setActiveTab] = useState('surveys');

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        {/* Page Header */}
        <div className="bg-card border-b border-border px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Icon name="ClipboardList" size={24} color="var(--color-primary)" />
                <h1 className="text-2xl font-semibold text-foreground">Surveys</h1>
              </div>
            </div>
            {activeTab === 'surveys' && (
              <Button
                variant="default"
                size="sm"
                onClick={() => { window.location.href = '/property-management'; }}
                iconName="Plus"
                iconPosition="left"
              >
                Assign Survey
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-card border-b border-border px-6">
          <nav className="flex space-x-0" aria-label="Tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center space-x-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <Icon name={tab.icon} size={15} />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'surveys'       && <SurveysTab />}
          {activeTab === 'swap_requests' && <SwapRequestsTab />}
          {activeTab === 'comments'      && <CommentsTab />}
        </div>
      </div>
    </div>
  );
};

export default Surveys;
